/**
 * v2 karşılaştırma sonucunun tel biçimi.
 *
 * `Backend/Kuika.Common/Document/Compare/Wire/CompareResultV2.cs` ile **birebir** eşleşir;
 * ikisi birlikte değişir. Sunucu tarafında sözleşme testleri var
 * (`CompareResultV2Tests`): alan adlarında `old`/`new` yok, bulgular önem ve güvene göre
 * SIRALI geliyor, koordinatlar normalize (0-1), madde ↔ bulgu indeksleri tutarlı.
 *
 * Arayüzün sıralama ya da filtreleme mantığı kurmasına gerek yok: sıra sözleşmenin parçası,
 * böylece iki tüketici aynı sırayı görüyor.
 */

/** `first` | `second` | `neither` — hangi taraf referans. */
type ReferenceSide = "first" | "second" | "neither";

export type FindingKind = "added" | "removed" | "changed";

/** Önem ekseni — güvenden BAĞIMSIZ. */
export type FindingSeverity = "formattingOnly" | "semantic" | "criticalValue";

export type DeltaMagnitudeV2 = "none" | "minor" | "moderate" | "major" | "orderOfMagnitude";

/** Güven katmanı — kullanıcıya gösterilen "kesin / şüpheli / zayıf" ayrımı. */
type ConfidenceTier = "certain" | "suspect" | "weak";

type ClauseStatus = "unchanged" | "changed" | "added" | "removed" | "moved";

/** İçerik durumundan AYRI eksen: madde bölündü mü, birleşti mi. */
type ClauseStructure = "intact" | "split" | "merged";

/** Bir tarafta konum. `rect` yoksa bulgu sayfada işaretlenemez (koordinat bilinmiyor). */
export interface LocatorV2 {
  /** 1-tabanlı sayfa numarası. */
  page: number;
  /**
   * SINIRLAYICI dikdörtgen [x, y, w, h] — 0-1, sayfa boyutuna oran. Pixel DEĞİL.
   *
   * Çizim için değil: birden çok satıra yayılan bir bulguda sınırlayıcı kutu satırların
   * arasındaki her şeyi de kapsıyor. Çizim `rects` ile yapılıyor.
   */
  rect: number[] | null;
  /**
   * SATIR BAŞINA dikdörtgenler — vurgu bunlarla çiziliyor, metin seçimi gibi.
   *
   * <p><b>Kapatılan üretim hatası.</b> Vurgu tek bir sınırlayıcı kutuyla çiziliyordu.
   * Altur sf1'de bir pazarlama görselinin içinden okunan dağınık token'lar tek bir bulgu
   * oldu ve kutusu sayfanın %48,9'unu kapladı: devasa, ve hiçbir yeri işaret etmiyor.
   * Aynı bulgu satır başına 12 kutuya ayrıldığında en büyüğü sayfanın %1,1'i.</p>
   *
   * Eski sunucular bu alanı göndermiyor; yoksa `rect`'e düşülüyor.
   */
  rects?: number[][] | null;
}

/** Güveni düşüren tek bir delil: adı, etkisi ve insan okunur gerekçesi. */
interface ConfidenceFactorV2 {
  name: string;
  multiplier: number;
  explanation: string;
}

export interface ConfidenceV2 {
  value: number;
  tier: ConfidenceTier;
  /** Boş liste "hiçbir şüphe yok" demek. */
  factors: ConfidenceFactorV2[];
}

/**
 * İki tarafın karşılık gelen bir noktası. Eşzamanlı kaydırma bunlar arasında
 * interpolasyon yapıyor.
 *
 * Motorun mantıksal düğüm eşleştirmelerinden türetiliyor. Oranlı eşleme (sol %30'daysa sağ
 * %30) sayfa sayıları farklı olduğunda çöker — korpusta alarko çiftinde sol 1 sayfa, sağ 2.
 */
export interface AnchorV2 {
  first: LocatorV2 | null;
  second: LocatorV2 | null;
  /** Bu çapanın dayandığı eşleşen kelime sayısı; zayıf çapalar elenebilir. */
  matchedWords: number;
}

export interface FindingV2 {
  kind: FindingKind;
  firstText: string;
  secondText: string;
  first: LocatorV2 | null;
  second: LocatorV2 | null;
  severity: FindingSeverity;
  magnitude: DeltaMagnitudeV2;
  /** Değişen değerin türü; sayısal değilse `none`. */
  valueKind: string;
  confidence: ConfidenceV2;
  /**
   * Bulgunun kelimelerinin ne kadarı dekoratif/sayfa mobilyası olarak etiketli [0,1];
   * 0 = temiz bulgu, 1 = tamamen bastırılmış.
   *
   * Bilgi `confidence.factors` içindeki `dekoratif-içerik` faktöründe de var (çarpanı
   * `1 − suppression`), ama oradan okumak sözleşmeyi insan okunur bir Türkçe etikete
   * bağlamak olurdu. Faktörler GEREKÇE taşır, veri taşımaz.
   */
  suppression: number;
  /**
   * Referans tarafta metin var, zayıf taraf (OCR) onu hiç üretmemiş — bulgu "silinmiş"
   * değil "okunamamış" olabilir. Kullanıcı için ayrı bir cümle: birincisi karar
   * gerektirir, ikincisi belgenin yeniden taranmasını.
   */
  likelyUnread: boolean;
  /**
   * İki metin yalnızca karışabilir gliflerle farklılaşıyor (ör. 1↔4) — gerçek değişiklik
   * değil OCR okuma hatası olabilir.
   */
  suspectedReadError: boolean;
  /** `clauses` dizisine indeksler. */
  clauseIndices: number[];
}

/** "Bu paragraf taşındı" — v1'in temsil edemediği yeteneklerden biri. */
export interface MoveV2 {
  text: string;
  wordCount: number;
  from: LocatorV2 | null;
  to: LocatorV2 | null;
  crossesPage: boolean;
  wordOrderChanged: boolean;
}

/**
 * Mantıksal birim türü — belgenin YAPISI.
 *
 * `paragraph` bir geri düşüş etiketi ("yapısal çapası olmayan paragraf"); geri kalan
 * her tür bir çapa taşıyor. Motor, bir taraf çapalı bir tür bilip diğeri bilmiyorsa
 * bilen tarafı seçiyor — bu yüzden taranmış bir belgede de `tableRow` görülebilir.
 */
type ClauseKind =
  | "title"
  | "clause"
  | "section"
  | "paragraph"
  | "list"
  | "listItem"
  | "table"
  | "tableRow"
  | "tableCell"
  | "field"
  | "headerFooter";

/** "Hangi madde değişti" — v1'in temsil edemediği yeteneklerden biri. */
export interface ClauseV2 {
  number: string;
  label: string;
  kind: ClauseKind;
  status: ClauseStatus;
  structure: ClauseStructure;
  /** 1-tabanlı sayfa; bilinmiyorsa 0. */
  page: number;
  structureConfidence: number;
  /** `findings` dizisine indeksler. */
  findingIndices: number[];
}

/**
 * İki taraflı delil kararı — bir bölgenin imza mı, iki tarafta da duran mobilya mı,
 * yoksa OCR'ın okuyamadığı basılı metin mi olduğu.
 */
export type SignatureVerdict =
  | "addedInk"
  | "presentOnBothSides"
  | "unreadText"
  | "noCounterpart"
  | "referenceSideIneligible";

/**
 * İmza / kaşe / paraf bölgesi.
 *
 * Liste imza sınıfı OLMAYAN bölgeleri de taşıyor (motor hiçbir şeyi silmiyor); ayrım
 * `isSignature` ile. Her kaydın kendi gerekçesi var, böylece "burada bir şey gördüm ama
 * imza saymadım, çünkü…" cevabı kullanıcıya ulaşıyor.
 */
export interface SignatureV2 {
  /** `signature` | `stamp` | `initials` | `handwriting` | `figure` | … */
  kind: string;
  side: "first" | "second";
  locator: LocatorV2 | null;
  verdict: SignatureVerdict | string;
  /** Kullanıcıya "imza" olarak sunulmalı mı — sunum kararı, varlık kararı değil. */
  isSignature: boolean;
  confidence: number;
  /** Karşı tarafta aynı alandaki token sayısı — kararın sayısal dayanağı. */
  counterpartTokens: number;
  evidence: string;
}

export interface CompareSideV2 {
  /** `Ooxml` | `PdfTextLayer` | `Ocr` | `PlainText`. */
  extractor: string;
  pageCount: number;
  wordCount: number;
  /** [0,1] — yalnızca iki tarafı KARŞILAŞTIRMAK için anlamlı. */
  reliabilityScore: number;
}

export interface ReferenceInfoV2 {
  side: ReferenceSide;
  /** Kararın gerekçesi; arayüzde açıklama olarak gösteriliyor. */
  explanation: string;
}

export interface CompareSummaryV2 {
  similarityPercent: number;
  added: number;
  removed: number;
  changed: number;
  moved: number;
  certain: number;
  suspect: number;
  weak: number;
  criticalValue: number;
  semantic: number;
  formattingOnly: number;
  changedClauses: number;
  restructuredClauses: number;
  /** Sıfırdan büyükse tam liste sunucuda DURUYOR — sessizce silinmedi (invariant I2). */
  suppressed: number;
}

export interface CompareResultV2 {
  schemaVersion: string;
  first: CompareSideV2;
  second: CompareSideV2;
  reference: ReferenceInfoV2;
  summary: CompareSummaryV2;
  findings: FindingV2[];
  moves: MoveV2[];
  clauses: ClauseV2[];
  /** İmza / kaşe bölgeleri. İmza sınıfı olanlar listenin başında. */
  signatures: SignatureV2[];
  /** Boş olmayan liste sonucun EKSİK olduğunu söyler (invariant I6). */
  /**
   * Hizalanmış konum çiftleri — eşzamanlı kaydırmanın dayanağı. BOŞ gelirse senkron
   * kapatılır ve kullanıcıya söylenir: sessizce yanlış hizalamak daha kötü.
   */
  anchors: AnchorV2[];
  degradedFeatures: string[];
  weightsSignature: string;
}

/**
 * Sonucun v2 sözleşmesi olup olmadığını söyler.
 *
 * Sürüm kontrolü ALAN VARLIĞINA değil `schemaVersion`'a bakıyor: alan varlığına bakmak,
 * sunucu yeni bir alan eklediğinde sessizce yanlış dala girmek demek olurdu.
 */
export function isCompareResultV2(value: CompareResultV2 | null | undefined): value is CompareResultV2 {
  return !!value && typeof value.schemaVersion === "string" && value.schemaVersion.startsWith("2.");
}

/** Güven katmanının rengi ve etiketi — çipler ve kenarlıklar bunu kullanıyor. */
interface TierStyle {
  color: string;
  labelTr: string;
  labelEn: string;
}

export const TIER_STYLES: Record<ConfidenceTier, TierStyle> = {
  certain: { color: "#b91c1c", labelTr: "Kesin", labelEn: "Certain" },
  suspect: { color: "#b45309", labelTr: "Şüpheli", labelEn: "Suspect" },
  weak: { color: "#6b7280", labelTr: "Zayıf", labelEn: "Weak" }
};

interface SeverityStyle {
  color: string;
  background: string;
  labelTr: string;
  labelEn: string;
}

/**
 * Önem eksenin renkleri. Güven ekseninden AYRI tutuluyor: ikisini tek bir renge yıkmak
 * "kritik ama şüpheli" ile "biçimsel ama kesin" arasını görünmez kılardı.
 */
export const SEVERITY_STYLES: Record<FindingSeverity, SeverityStyle> = {
  criticalValue: { color: "#991b1b", background: "#fee2e2", labelTr: "Kritik değer", labelEn: "Critical value" },
  semantic: { color: "#9a3412", background: "#ffedd5", labelTr: "Anlam", labelEn: "Semantic" },
  formattingOnly: { color: "#4b5563", background: "#f3f4f6", labelTr: "Biçim", labelEn: "Formatting" }
};

/** İki dilde tek bir etiket. Satır içi obje tipi yerine adlandırılmış arayüz. */
export interface BilingualLabel {
  tr: string;
  en: string;
}

const MAGNITUDE_LABELS: Record<DeltaMagnitudeV2, BilingualLabel> = {
  none: { tr: "", en: "" },
  minor: { tr: "küçük", en: "minor" },
  moderate: { tr: "orta", en: "moderate" },
  major: { tr: "büyük", en: "major" },
  orderOfMagnitude: { tr: "mertebe", en: "order of magnitude" }
};

/** İki dilde etiket + renk. */
interface BilingualStatusLabel extends BilingualLabel {
  color: string;
}

const CLAUSE_STATUS_LABELS: Record<ClauseStatus, BilingualStatusLabel> = {
  unchanged: { tr: "aynı", en: "unchanged", color: "#6b7280" },
  changed: { tr: "değişti", en: "changed", color: "#b45309" },
  added: { tr: "eklendi", en: "added", color: "#15803d" },
  removed: { tr: "silindi", en: "removed", color: "#b91c1c" },
  moved: { tr: "taşındı", en: "moved", color: "#1d4ed8" }
};

const CLAUSE_STRUCTURE_LABELS: Record<ClauseStructure, BilingualLabel> = {
  intact: { tr: "", en: "" },
  split: { tr: "bölündü", en: "split" },
  merged: { tr: "birleşti", en: "merged" }
};

/**
 * Numarası olmayan bir birimin görünen adı. Numarası olan birimlerde numara gösterilir;
 * bu tablo yalnızca numarasızlar için devreye giriyor ve ham enum adının kullanıcıya
 * sızmasını engelliyor ("TableRow" değil "tablo satırı").
 */
/**
 * İmza bölgesi türünün görünen adı. Ham enum adı ("Handwriting") kullanıcıya sızmamalı;
 * liste `figure` gibi imza sınıfı OLMAYAN türleri de içeriyor çünkü motor onları da
 * gerekçesiyle taşıyor.
 */
export const SIGNATURE_KIND_LABELS: Record<string, BilingualLabel> = {
  signature: { tr: "imza", en: "signature" },
  stamp: { tr: "kaşe", en: "stamp" },
  initials: { tr: "paraf", en: "initials" },
  handwriting: { tr: "el yazısı", en: "handwriting" },
  figure: { tr: "görsel", en: "figure" },
  logo: { tr: "logo", en: "logo" },
  barcode: { tr: "barkod", en: "barcode" },
  line: { tr: "çizgi", en: "line" }
};

/**
 * İki taraflı delil kararının kullanıcıya anlatımı. Kararın KENDİSİ değil GEREKÇESİ
 * kullanıcıyı ikna eder; motor her kayıt için ayrıca serbest metin gerekçe de veriyor.
 */
export const SIGNATURE_VERDICT_LABELS: Record<string, BilingualLabel> = {
  addedInk: { tr: "imzalanırken eklenmiş", en: "added when signed" },
  noCounterpart: { tr: "karşı tarafta yok", en: "absent on the other side" },
  presentOnBothSides: { tr: "iki tarafta da var", en: "present on both sides" },
  unreadText: { tr: "okunamamış basılı metin", en: "unread printed text" },
  referenceSideIneligible: { tr: "referans taraf imza taşıyamaz", en: "reference side cannot carry signatures" }
};

const CLAUSE_KIND_LABELS: Record<ClauseKind, BilingualLabel> = {
  title: { tr: "başlık", en: "title" },
  clause: { tr: "madde", en: "clause" },
  section: { tr: "bölüm", en: "section" },
  paragraph: { tr: "paragraf", en: "paragraph" },
  list: { tr: "liste", en: "list" },
  listItem: { tr: "liste öğesi", en: "list item" },
  table: { tr: "tablo", en: "table" },
  tableRow: { tr: "tablo satırı", en: "table row" },
  tableCell: { tr: "tablo hücresi", en: "table cell" },
  field: { tr: "alan", en: "field" },
  headerFooter: { tr: "üst/alt bilgi", en: "header/footer" }
};
